/**
 * GiftCardVaultPanel (iter134) — Master-admin surface embedded in the
 * active campaign card. Shows inventory (available / issued / delivered
 * / total, plus master-only total_cost), accepts a CSV upload of new
 * codes, and refreshes summary on completion.
 *
 * Every code is Fernet-encrypted server-side. The frontend never sees
 * a plaintext code — only counts and per-customer send-log rows.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Upload, AlertTriangle, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function GiftCardVaultPanel({ campaign }) {
  const [summary, setSummary] = useState(null);
  const [uploadState, setUploadState] = useState({ busy: false, result: null, error: null });
  const fileRef = useRef(null);

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const loadSummary = useCallback(async () => {
    if (!campaign?.id || !token) return;
    try {
      const { data } = await axios.get(
        `${API}/admin/campaigns/${campaign.id}/gift-cards/summary`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSummary(data);
    } catch (e) {
      setSummary(null);
    }
  }, [campaign?.id, token]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const upload = async (file) => {
    if (!file || !campaign?.id) return;
    setUploadState({ busy: true, result: null, error: null });
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await axios.post(
        `${API}/admin/campaigns/${campaign.id}/gift-cards/upload`,
        form,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setUploadState({ busy: false, result: data, error: null });
      await loadSummary();
    } catch (e) {
      const msg =
        e?.response?.data?.detail || e?.message || "Upload failed";
      setUploadState({ busy: false, result: null, error: msg });
    }
  };

  const denom = campaign?.denomination_cents || 0;
  const denomLabel = `$${Math.round(denom / 100)}`;

  const lowThreshold = Math.max(5, Math.round((summary?.total || 0) * 0.1));
  const isLow = summary && summary.available > 0 && summary.available <= lowThreshold;
  const isEmpty = summary && summary.available === 0 && summary.total > 0;

  return (
    <div
      className="mt-3 border-t border-red-200 pt-3 space-y-3"
      data-testid="admin-td-vault-panel"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[12px] font-semibold text-red-900 flex items-center gap-2">
            Gift-card inventory
            {isEmpty && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                <AlertTriangle size={11} /> STOCKOUT
              </span>
            )}
            {isLow && !isEmpty && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                <AlertTriangle size={11} /> LOW
              </span>
            )}
          </div>
          {summary && (
            <div className="text-[11px] text-red-700 mt-0.5">
              <b>{summary.available}</b> available · {summary.issued} issued
              {summary.delivered ? ` · ${summary.delivered} delivered` : ""}
              {summary.total_cost_cents ? ` · $${(summary.total_cost_cents / 100).toFixed(2)} spent` : ""}
            </div>
          )}
          {!summary && (
            <div className="text-[11px] text-red-600 mt-0.5">Loading inventory…</div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid="admin-td-vault-upload-file"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploadState.busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50"
            data-testid="admin-td-vault-upload-btn"
          >
            {uploadState.busy ? (
              <><Loader2 size={13} className="animate-spin" /> Uploading…</>
            ) : (
              <><Upload size={13} /> Upload {denomLabel} codes CSV</>
            )}
          </button>
        </div>
      </div>

      {uploadState.result && (
        <div className="text-[11px] bg-white border border-red-200 rounded p-2 space-y-1">
          <div className="font-semibold text-green-700">
            {uploadState.result.accepted} accepted at {denomLabel} each
            {uploadState.result.rejected > 0 && (
              <span className="text-amber-700">
                {" · "}{uploadState.result.rejected} rejected
              </span>
            )}
          </div>
          {uploadState.result.reasons?.length > 0 && (
            <ul className="text-[10px] text-red-700 list-disc list-inside">
              {uploadState.result.reasons.slice(0, 5).map((r, i) => (
                <li key={i}>{r}</li>
              ))}
              {uploadState.result.reasons.length > 5 && (
                <li className="italic">…and {uploadState.result.reasons.length - 5} more</li>
              )}
            </ul>
          )}
        </div>
      )}
      {uploadState.error && (
        <div className="text-[11px] text-red-800 bg-red-100 border border-red-300 rounded p-2">
          {String(uploadState.error)}
        </div>
      )}
      <div className="text-[10px] text-red-800/70 leading-tight">
        CSV columns: <code className="bg-red-100 px-1 rounded">code, denomination_dollars</code>
        (optional: <code className="bg-red-100 px-1 rounded">cost_dollars</code>). Only {denomLabel} codes are
        accepted for this campaign — mismatched denominations are rejected line-by-line.
      </div>
    </div>
  );
}
